import React, { useContext, useEffect } from 'react';

import { ETypeSector, Partner, PartnerApi, PartnerDetails, PartnerTypes } from 'apis/PartnerApi';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { GMetrixTable } from 'components/GMetrixTable/GMetrixTable';
import { Alert, Col, Collapse, Row } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePartners } from 'pages/partners/context';
import { useSuperState } from 'hooks';

type MergeSearchStepProps = {
    onSelect: (partner: Partner | null) => void;
    selectedTarget: PartnerDetails | null;
    sourcePartner: PartnerDetails;
    targetLoading: boolean;
};

export const MergeSearchStep = (props: MergeSearchStepProps) => {

    const { onSelect, selectedTarget, sourcePartner, targetLoading } = props;

    const { countries, states } = usePartners();
    const { formatMessage } = useIntl();

    const sectorOptions = [
        { Id: 0, Name: formatMessage({ id: 'all_sectors.734702a7', defaultMessage: 'All Sectors' }) },
        { Id: ETypeSector.K12, Name: formatMessage({ id: 'k12_education.eccc589e', defaultMessage: 'K12 Education' }) || 'K-12 Education' },
        { Id: ETypeSector.Government, Name: formatMessage({ id: 'government.28df1366', defaultMessage: 'Government' }) || 'Government' },
        { Id: ETypeSector.Commercial, Name: formatMessage({ id: 'commercial.431dfb1b', defaultMessage: 'Commercial' }) || 'Commercial' },
        { Id: ETypeSector.HigherEducation, Name: formatMessage({ id: 'higher_education.64009dd3', defaultMessage: 'Higher Education' }) || 'Higher Education' }
    ];

    const statusOptions = [
        { Id: -1, Name: formatMessage({ id: 'all.6feedc20', defaultMessage: 'All' }) },
        { Id: 1, Name: formatMessage({ id: 'active.b9616c88', defaultMessage: 'Active' }) || 'Active' },
        { Id: 0, Name: formatMessage({ id: 'inactive.5ee46a1c', defaultMessage: 'Inactive', description: 'New' }) || 'Inactive' }
    ];

    const [state, setState] = useSuperState({
        allPartners: [] as Partner[],
        error: '',
        filterCountry: 0,
        filterSearch: '',
        filterSector: 0,
        filterState: 0,
        filterStatus: -1,
        loading: true,
        showAdvanced: false
    });

    const loadPartners = async () => {

        try {

            setState({ error: '', loading: true });
            const partners = await PartnerApi.GetAll(PartnerTypes.TestingCenter);
            const filtered = partners.filter(p => p.ID !== sourcePartner.ID);
            setState({ allPartners: filtered, loading: false });

        } catch (e: any) {

            setState({ error: e?.message || (formatMessage({ id: 'an_error_has_occurred_try_again_or_conta.b1a18880', defaultMessage: 'An error has occurred, try again or contact us for further assistance', description: 'Mensaje' }) || 'Failed to load Testing Centers.'), loading: false });

        }

    };

    useEffect(() => {

        loadPartners();

    }, []);

    const filteredPartners = state.allPartners.filter((p: any) => {

        const search = state.filterSearch.toLowerCase().trim();
        if (search) {

            const matchesName = p.Name?.toLowerCase().includes(search);
            const matchesEmail = p.ContactEmail?.toLowerCase().includes(search);
            const matchesCustomerId = p.CustomerIDString?.toLowerCase().includes(search);
            const matchesId = String(p.ID) === search;
            if (!matchesName && !matchesEmail && !matchesCustomerId && !matchesId) return false;

        }

        if (state.filterSector !== 0 && p.Sector !== state.filterSector) return false;

        if (state.filterStatus !== -1) {

            const isActive = state.filterStatus === 1;
            if (p.Active !== isActive) return false;

        }

        if (state.filterCountry !== 0 && p.Country?.Id !== state.filterCountry) return false;

        if (state.filterState !== 0 && p.StateID !== state.filterState) return false;

        return true;

    });

    const columns = [
        { field: 'Name', title: formatMessage({ id: 'organization.7cb537a9', defaultMessage: 'Organization', description: 'New' }) || 'Organization' },
        { field: 'ContactEmail', title: formatMessage({ id: 'email_contact.93dfadf4', defaultMessage: 'Email Contact', description: 'New' }) || 'Email Contact' },
        { field: 'CustomerIDString', title: formatMessage({ id: 'customer_id.16ab92c3', defaultMessage: 'Customer ID', description: 'New' }) || 'Customer ID' },
        { field: 'DateCreate', title: formatMessage({ id: 'created.8d9f8791', defaultMessage: 'Created' }) }
    ];

    const handleRowSelect = (selected: Partner[]) => {

        if (selected.length > 0) {

            onSelect(selected[0]);

        } else {

            onSelect(null);

        }

    };

    const handleClearFilters = () => {

        setState({
            filterCountry: 0,
            filterSearch: '',
            filterSector: 0,
            filterState: 0,
            filterStatus: -1
        });

    };

    const countryOptions = [{ Id: 0, Name: formatMessage({ id: 'all_countries.abfbbb09', defaultMessage: 'All Countries' }) || 'All Countries' }, ...countries];
    const stateOptions = [{ Id: 0, Name: formatMessage({ id: 'all_states.84b68b6a', defaultMessage: 'All States' }) || 'All States' }, ...states];

    return (
        <div aria-label={formatMessage({ id: 'merge_testing_center.b2fbfd99', defaultMessage: 'Merge Testing Center', description: 'Link Name in Left Menu' }) || 'Merge Testing Center'} className="merge-search-step mt-3" role="region">
            <Alert className="d-flex align-items-start" variant="info">
                <i className="fa-solid fa-building me-2 mt-1" />
                <div>
                    <strong>
                        {<FormattedMessage defaultMessage="Account to Keep" id="account_to_keep.980b996d" /> || 'Account to Keep'}
                    </strong>
                    <br />
                    {sourcePartner.Name}
                    {' — '}
                    <span className="text-muted">
                        {sourcePartner.ContactEmail}
                        {' · ID: '}
                        {sourcePartner.ID}
                    </span>
                </div>
            </Alert>

            <h6 className="fw-bold mb-3">
                {<FormattedMessage defaultMessage="General Filters" description="New" id="general_filters.2e4a710e" /> || 'General Filters'}
            </h6>

            <Row className="mb-2">
                <Col lg={6} xs={12}>
                    <GMetrixInput
                        label={formatMessage({ id: 'search.0ab06b2c', defaultMessage: 'Search' }) || 'Search'}
                        onChange={(e: any) => setState({ filterSearch: e.target.value || '' })}
                        placeholder={formatMessage({ id: 'search_by_organization_email_or_customer.f09a336b', defaultMessage: 'Search by organization, email, or customer ID...' })}
                        type="text"
                        value={state.filterSearch}
                    />
                </Col>
            </Row>

            <div className="mb-2">
                <GMetrixButton
                    aria-controls="merge-advanced-filters"
                    aria-expanded={state.showAdvanced}
                    onClick={() => setState({ showAdvanced: !state.showAdvanced })}
                    size="sm"
                    variant="link"
                >
                    <i className={`fa-solid ${state.showAdvanced ? 'fa-minus' : 'fa-plus'} me-1`} />
                    {<FormattedMessage defaultMessage="Advanced Filters" description="New" id="advanced_filters.b6af7927" /> || 'Advanced Filters'}
                </GMetrixButton>
            </div>

            <Collapse in={state.showAdvanced}>
                <div id="merge-advanced-filters">
                    <Row className="mb-2">
                        <Col lg={3} xs={12}>
                            <GMetrixDropdown
                                dataItemKey="Id"
                                dataItems={sectorOptions}
                                label={formatMessage({ id: 'sector.343b1273', defaultMessage: 'Sector', description: 'New' }) || formatMessage({ id: 'sector.99a79448', defaultMessage: 'Sector' }) || 'Sector'}
                                onChange={(e: any) => setState({ filterSector: e.Id })}
                                textField="Name"
                                value={sectorOptions.find(s => s.Id === state.filterSector) || sectorOptions[0]}
                            />
                        </Col>
                        <Col lg={3} xs={12}>
                            <GMetrixDropdown
                                dataItemKey="Id"
                                dataItems={countryOptions}
                                label={formatMessage({ id: 'country.05543f81', defaultMessage: 'Country', description: 'New' }) || formatMessage({ id: 'country.cfeb54cc', defaultMessage: 'Country' }) || 'Country'}
                                onChange={(e: any) => setState({ filterCountry: e.Id })}
                                textField="Name"
                                value={countryOptions.find(c => c.Id === state.filterCountry) || countryOptions[0]}
                            />
                        </Col>
                        <Col lg={3} xs={12}>
                            <GMetrixDropdown
                                dataItemKey="Id"
                                dataItems={statusOptions}
                                label={formatMessage({ id: 'status.ad856ad7', defaultMessage: 'Status' }) || 'Status'}
                                onChange={(e: any) => setState({ filterStatus: e.Id })}
                                textField="Name"
                                value={statusOptions.find(s => s.Id === state.filterStatus) || statusOptions[0]}
                            />
                        </Col>
                        <Col lg={3} xs={12}>
                            <GMetrixDropdown
                                dataItemKey="Id"
                                dataItems={stateOptions}
                                label={formatMessage({ id: 'stateprovince.d2dfa3c8', defaultMessage: 'State/Province' })}
                                onChange={(e: any) => setState({ filterState: e.Id })}
                                textField="Name"
                                value={stateOptions.find(s => s.Id === state.filterState) || stateOptions[0]}
                            />
                        </Col>
                    </Row>
                </div>
            </Collapse>

            <div className="d-flex gap-2 mb-3">
                <GMetrixButton
                    onClick={handleClearFilters}
                    size="sm"
                    variant="outline-secondary"
                >
                    {<FormattedMessage defaultMessage="Clear" id="clear.88d43eb1" /> || 'Clear Filters'}
                </GMetrixButton>
            </div>

            {state.loading ? (
                <GMetrixLoader />
            ) : state.error ? (
                <Alert role="alert" variant="danger">
                    <i className="fa-solid fa-circle-exclamation me-2" />
                    {state.error}
                </Alert>
            ) : (
                <>
                    {targetLoading && (
                        <Alert className="d-flex align-items-center" role="status" variant="info">
                            <i className="fa-solid fa-spinner fa-spin me-2" />
                            {<FormattedMessage defaultMessage="Loading..." description="Mensaje" id="loading.6f38d7c8" /> || 'Loading details...'}
                        </Alert>
                    )}
                    {!targetLoading && selectedTarget && (
                        <Alert className="d-flex align-items-center" role="status" variant="success">
                            <i className="fa-solid fa-check-circle me-2" />
                            {<FormattedMessage defaultMessage="Account to Merge" id="account_to_merge.20112764" /> || 'Selected target'}
                            :
                            {' '}
                            <strong className="ms-1">
                                {selectedTarget.Name}
                            </strong>
                            <span className="ms-1 text-muted">
                                <FormattedMessage defaultMessage="(ID:" id="id.1777245d" />
                                {' '}
                                {selectedTarget.ID}
                                )
                            </span>
                        </Alert>
                    )}
                    <GMetrixTable
                        selectableRows
                        columnData={columns}
                        multiSelect={false}
                        onSelectedChange={handleRowSelect}
                        rowData={filteredPartners}
                        selected={selectedTarget ? [selectedTarget] : []}
                        tableName="merge-tc-search"
                    />
                    {filteredPartners.length === 0 && (
                        <div className="text-center text-muted py-4" role="status">
                            <i className="fa-solid fa-search me-1" />
                            {<FormattedMessage defaultMessage="No Results Found" id="no_results_found.f1d71249" /> || 'No Results Found'}
                        </div>
                    )}
                </>
            )}
        </div>
    );

};
